import React, { useEffect, useState } from 'react';
import { fetchAdminAccounts, createAdminAccount, type AdminAccount } from '../lib/userDetailService';
import { disableAccount, resetPassword } from '../lib/userService';
import { useAuth } from '../lib/auth';
import { logger } from '../lib/logger';

const AdminAccounts: React.FC = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Création
  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);

  // Reset password
  const [resetModal, setResetModal] = useState<AdminAccount | null>(null);
  const [resetPwd, setResetPwd] = useState('');

  const load = () => {
    fetchAdminAccounts()
      .then(setAdmins)
      .catch((err) => {
        logger.error('load_admins', 'AdminAccounts', { error: String(err) });
        setError(String(err));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createAdminAccount(newEmail.trim(), newPassword);
      setCreateOpen(false);
      setNewEmail('');
      setNewPassword('');
      setLoading(true);
      load();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleSuspend = async (a: AdminAccount) => {
    if (a.userId === user?.id) {
      alert('Impossible de suspendre votre propre compte.');
      return;
    }
    const willSuspend = a.status !== 'suspended';
    if (willSuspend && !confirm(`Suspendre le compte admin ${a.email} ?`)) return;
    setActionLoading(a.userId);
    try {
      await disableAccount(a.userId, willSuspend);
      setAdmins((prev) =>
        prev.map((x) => (x.userId === a.userId ? { ...x, status: willSuspend ? 'suspended' : 'active' } : x))
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPwd = async () => {
    if (!resetModal || resetPwd.length < 8) return;
    setActionLoading(resetModal.userId);
    try {
      await resetPassword(resetModal.userId, resetPwd);
      alert('Mot de passe réinitialisé.');
      setResetModal(null);
      setResetPwd('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-20 text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">🔐</span>
          <div>
            <p className="text-xl font-bold text-gray-900">{admins.length}</p>
            <p className="text-xs text-gray-500">Comptes admin</p>
          </div>
        </div>
        <button
          onClick={() => { setCreateOpen(true); setNewEmail(''); setNewPassword(''); }}
          className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg"
        >
          + Créer un admin
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Statut</th>
              <th className="px-5 py-3">Créé le</th>
              <th className="px-5 py-3">Dernière connexion</th>
              <th className="px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.userId} className="border-t border-gray-100">
                <td className="px-5 py-3 font-medium text-gray-800">
                  {a.email}
                  {a.userId === user?.id && (
                    <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold rounded bg-blue-100 text-blue-700 uppercase">vous</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    a.status === 'suspended' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
                  }`}>
                    {a.status === 'suspended' ? 'Suspendu' : 'Actif'}
                  </span>
                </td>
                <td className="px-5 py-3 text-gray-500">{a.createdAt}</td>
                <td className="px-5 py-3 text-gray-500">{a.lastLoginAt || 'jamais'}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    {a.userId !== user?.id && (
                      <button
                        onClick={() => handleToggleSuspend(a)}
                        disabled={actionLoading === a.userId}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md ${
                          a.status === 'suspended' ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                        } disabled:opacity-40`}
                      >
                        {a.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
                      </button>
                    )}
                    <button
                      onClick={() => { setResetModal(a); setResetPwd(''); }}
                      className="px-2.5 py-1 text-xs font-medium text-[#00244F] bg-blue-50 hover:bg-blue-100 rounded-md"
                    >
                      Mdp
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal création */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setCreateOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Créer un compte admin</h3>
              <p className="text-sm text-gray-500 mt-1">
                Ce compte aura accès à tout le backoffice. Il n'est ni candidat ni recruteur.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="admin@exemple.fr"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg">
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || newPassword.length < 8 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim())}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-40"
              >
                {creating ? 'Création…' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal reset password */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setResetModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le mot de passe</h3>
              <p className="text-sm text-gray-500 mt-1">{resetModal.email}</p>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                placeholder="Nouveau mot de passe (min. 8 caractères)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setResetModal(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg">
                Annuler
              </button>
              <button
                onClick={handleResetPwd}
                disabled={resetPwd.length < 8 || actionLoading === resetModal.userId}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-40"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccounts;
